import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
export class MsalAuthenticationContext {
    constructor(msalPublicClientApplication, showInteractiveLoginRequestFunction, showInteractiveLogoutRequestFunction) {
        Object.defineProperty(this, "msalPublicClientApplication", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showInteractiveLoginRequestFunction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showInteractiveLogoutRequestFunction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onMsalActiveAccountChangedHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "invokeOnActiveAccountUpdated", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (account) => {
                if (this.onMsalActiveAccountChangedHandler) {
                    this.onMsalActiveAccountChangedHandler.onActiveMsalAccountChanged(account);
                }
            }
        });
        Object.defineProperty(this, "setActiveAccount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (authenticationResult) => {
                console.log("Setting active account with Authentication Result ", authenticationResult);
                const account = authenticationResult.account;
                this.msalPublicClientApplication.setActiveAccount(account);
                await this.invokeOnActiveAccountUpdated(account);
            }
        });
        Object.defineProperty(this, "initialize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await this.msalPublicClientApplication.initialize();
            }
        });
        Object.defineProperty(this, "getActiveAccount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const result = this.msalPublicClientApplication.getActiveAccount();
                return result ?? undefined;
            }
        });
        Object.defineProperty(this, "logOutWithUserInteractionIfRequired", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (logOutRequest) => {
                try {
                    await this.msalPublicClientApplication.logoutPopup(logOutRequest);
                    const activeAccount = this.getActiveAccount();
                    await this.invokeOnActiveAccountUpdated(activeAccount);
                }
                catch (error) {
                    if (error instanceof BrowserAuthError) {
                        if (error.errorCode === "popup_window_error") {
                            this.showInteractiveLogoutRequestFunction();
                        }
                        if (error.errorCode === "monitor_window_timeout") {
                            await this.msalPublicClientApplication.logoutRedirect();
                        }
                        else if (error.errorCode === "user_cancelled") {
                            // Do nothing. The user cancelled the log out.
                        }
                    }
                    else {
                        throw error;
                    }
                }
            }
        });
        Object.defineProperty(this, "logOutActiveAccount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (logOutRequest) => {
                const activeAccount = this.getActiveAccount();
                logOutRequest = logOutRequest ?? {};
                logOutRequest.account = activeAccount;
                logOutRequest.logoutHint = activeAccount?.idTokenClaims?.login_hint;
                logOutRequest.postLogoutRedirectUri = logOutRequest.postLogoutRedirectUri ?? "/";
                logOutRequest.mainWindowRedirectUri = logOutRequest.mainWindowRedirectUri ?? "/";
                await this.logOutWithUserInteractionIfRequired(logOutRequest);
            }
        });
        Object.defineProperty(this, "handleRedirect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const result = await this.msalPublicClientApplication.handleRedirectPromise();
                if (result?.account) {
                    this.setActiveAccount(result);
                }
                return result;
            }
        });
        // Attempts to acquire a token silently. If that fails, it tries to acquire it using a popup.
        Object.defineProperty(this, "acquireTokenForActiveAccountSilentlyOrWithPopupIfInteractionRequired", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (scopes) => {
                const activeAccount = this.getActiveAccount();
                if (activeAccount) {
                    const silentLoginRequest = {
                        scopes,
                        account: activeAccount
                    };
                    try {
                        const result = await this.msalPublicClientApplication.acquireTokenSilent(silentLoginRequest);
                        await this.setActiveAccount(result);
                        return result;
                    }
                    catch (error) {
                        if (error instanceof InteractionRequiredAuthError) {
                            const result = await this.acquireTokenPopupWithUserInteractionIfRequired(scopes);
                            return result;
                        }
                        else if (error instanceof BrowserAuthError) {
                            if (error.errorCode === "monitor_window_timeout") {
                                const redirectLoginRequest = {
                                    scopes,
                                    account: activeAccount
                                };
                                await this.msalPublicClientApplication.acquireTokenRedirect(redirectLoginRequest);
                            }
                            else {
                                throw error;
                            }
                        }
                        else {
                            throw error;
                        }
                    }
                }
                else {
                    const result = await this.acquireTokenPopupWithUserInteractionIfRequired(scopes);
                    return result;
                }
            }
        });
        // Attempts to acquire a token silently. If that fails, it tries to acquire it using a popup. If that fails because popups are blocked, it will invoke the showInteractiveLogingRequestFunction which should show an interactive element to the user asking to log in again.
        // The continuation will be the function that needs to be invoked when the user accepts the request.
        Object.defineProperty(this, "acquireTokenPopup", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (scopes, account) => {
                const loginRequest = {
                    scopes,
                    account
                };
                const acquireTokenPopupResult = await this.msalPublicClientApplication.acquireTokenPopup(loginRequest);
                await this.setActiveAccount(acquireTokenPopupResult);
                return acquireTokenPopupResult;
            }
        });
        // Attempts to acquire a token silently. If that fails, it tries to acquire it using a popup.
        // If that fails because popups are blocked, it will invoke the showInteractiveLogingRequestFunction which should show an interactive element to the user asking to log in again.
        // It is the responsibility of this interactive element to invoke acquireTokenPopup when the user accepts the request.
        Object.defineProperty(this, "acquireTokenPopupWithUserInteractionIfRequired", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (scopes, account) => {
                try {
                    const result = await this.acquireTokenPopup(scopes, account);
                    return result;
                }
                catch (error) {
                    if (error instanceof BrowserAuthError && error.errorCode === "popup_window_error") {
                        this.showInteractiveLoginRequestFunction();
                    }
                    else {
                        throw error;
                    }
                }
            }
        });
        Object.defineProperty(this, "setOnMsalActiveAccountChangedHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (handler) => {
                this.onMsalActiveAccountChangedHandler = handler;
            }
        });
        Object.defineProperty(this, "dispose", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.setOnMsalActiveAccountChangedHandler();
            }
        });
        this.msalPublicClientApplication = msalPublicClientApplication;
        this.showInteractiveLoginRequestFunction = showInteractiveLoginRequestFunction;
        this.showInteractiveLogoutRequestFunction = showInteractiveLogoutRequestFunction;
    }
}
